import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ImageOff, Crosshair, Check, X, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { numberLocale } from '@/lib/language';
import { getScoreTextColor } from '@/components/optimization-score/score-colors';
import { mostUrgentWarning, warningLabel } from './ValidationNotice';
import { ProductStatusPill } from './ProductStatusPill';
import { ProductThumb } from './ProductThumb';

/**
 * Das Vorschaubild einer Leistenzeile. Teller, Platzhalter und
 * Ladefehler-Behandlung liegen seit dem 09.09.2026 in `ProductThumb` -- die
 * Listenansicht braucht dieselben drei Dinge, und ihre eigene Fassung hatte
 * genau das dritte nicht (sie versteckte ein kaputtes Bild per
 * `style.display = 'none'`). Zwei Kopien, von denen eine hinterherhinkt, sind
 * der Grund fuer die gemeinsame Datei.
 */
function RailThumb({ imageLink }: { imageLink: string | null }) {
  return (
    <ProductThumb
      imageLink={imageLink}
      groesse="h-[30px] w-[30px]"
      symbolGroesse="h-4 w-4"
      testIdPraefix="rail"
    />
  );
}

import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { groupRailRows, type RailRow } from '@/lib/railGrouping';
import type { WorkbenchProduct, ReviewDecision } from '@/hooks/useWorkbench';

/**
 * Feste Zeilenhoehe. Bewusst fest und nicht gemessen: eine variable Hoehe
 * zwingt den Virtualizer zur Nachmessung, und die Scrollbindung unten
 * (`scrollToIndex`) wird damit unzuverlaessig — sie zielt auf eine Position,
 * die sich beim Messen noch verschiebt.
 *
 * 44 statt der frueheren 56: der Titel steht seit T2 einzeilig (`truncate`
 * statt `line-clamp-2`), die Zeile braucht also keine zwei Textzeilen mehr.
 */
const ROW_HEIGHT = 44;

interface WorkbenchRailProps {
  products: WorkbenchProduct[];
  /** Das Produkt, das rechts im Detailbereich steht. */
  currentId: string | null;
  onSelect: (id: string) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  getReviewDecision: (id: string) => ReviewDecision;
  loadedCount: number;
  /** Feed-weite Zahl. `null` heisst "unbekannt", nicht "null". */
  pendingTotal: number | null;
  hasNextPage: boolean;
  onLoadMore: () => void;
  /**
   * Sind die Auswahlboxen eingeblendet?
   *
   * Seit Spec 9.4 die EINZIGE Bedingung dafuer, in jeder Breite. Das frueher
   * hier stehende `isNarrow` ist ersatzlos entfallen: es steuerte nur noch die
   * Boxen und den Schalter im Kopf, und beide haengen jetzt an dieser Angabe
   * beziehungsweise an `onSelectionModeChange`. Ein Wert, den niemand mehr
   * liest, waere spaetestens beim naechsten Umbau eine falsche Faehrte.
   */
  selectionMode?: boolean;
  onSelectionModeChange?: (on: boolean) => void;
  /**
   * Sammelhandlungen. Bekommen ausschliesslich Produkte, die in der geladenen
   * Menge stehen UND noch unentschieden sind — die Leiste filtert vorher, damit
   * eine ueber Reiter- oder Filterwechsel stehengebliebene Auswahl nichts
   * anfassen kann.
   */
  onBulkApprove?: (ids: string[]) => void;
  onBulkReject?: (ids: string[]) => void;
}

/**
 * Die Produktleiste der geteilten Ansicht.
 *
 * **Ein Verzeichnis, keine Leseflaeche.** 300 px fassen rund 60 Zeichen,
 * optimierte Titel laufen 90 bis 150 — also genau das, was geprueft wird. Wer
 * aus der Leiste heraus urteilt, urteilt auf einem abgeschnittenen Titel.
 * Gelesen wird rechts, und keine Beschriftung hier darf etwas anderes
 * suggerieren: der Titel ist einzeilig gekuerzt (`truncate`, seit T2 —
 * vorher zweizeilig) und traegt keinen Tooltip, der den Volltext nachreicht.
 */
export function WorkbenchRail({
  products,
  currentId,
  onSelect,
  selectedIds,
  onToggleSelect,
  getReviewDecision,
  loadedCount,
  pendingTotal,
  hasNextPage,
  onLoadMore,
  selectionMode = false,
  onSelectionModeChange,
  onBulkApprove,
  onBulkReject,
}: WorkbenchRailProps) {
  const { t, i18n } = useTranslation('workbench');

  /* Wie der Auslieferungsstand in der ZEILE erscheint. Die Karte zeigt ihn
     unabhaengig davon immer im Klartext. Ausserhalb der Entwurfsseite liefert
     der Haken 'pille', also den heutigen Zustand. */
  const { statusAnzeige, listenKachel } = useEntwurf();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [confirmBulkReject, setConfirmBulkReject] = useState(false);

  /**
   * Varianten-Gruppierung (T2/W3), rein clientseitig auf dem geladenen
   * Puffer — keine neue Abfrage, `groupRailRows` sieht nur, was hier schon
   * steht. `allRows` enthaelt JEDES Produkt (gruppiert oder nicht); welche
   * Zeilen tatsaechlich gerendert werden, entscheidet `offeneGruppen` unten.
   */
  const { rows: allRows, groupOf } = useMemo(() => groupRailRows(products), [products]);

  /** Welche Gruppen der Nutzer per Klick oder Pfeiltaste geoeffnet hat. */
  const [offeneGruppen, setOffeneGruppen] = useState<Set<string>>(new Set());

  /**
   * Steht der Cursor in einer geschlossenen Gruppe, gilt sie fuer das
   * RENDERN als offen — abgeleitet bei jedem Render, KEIN Effect. Ein
   * Effect, der das in `offeneGruppen` schreibt, wuerde bei einem manuellen
   * Zuklappen sofort erneut ausloesen, solange der Cursor dort noch steht,
   * und damit staendig gegen den Nutzer arbeiten, der die Gruppe gerade
   * geschlossen hat. Die Ableitung hier hat denselben sichtbaren Effekt
   * (die Gruppe des Cursors bleibt sichtbar offen), aber ohne einen
   * zusaetzlichen Render-Zyklus und ohne den Zustand dauerhaft zu verunreinigen.
   */
  const effectiveOpenGroups = useMemo(() => {
    if (!currentId) return offeneGruppen;
    const cursorGroup = groupOf.get(currentId);
    if (!cursorGroup || offeneGruppen.has(cursorGroup)) return offeneGruppen;
    const next = new Set(offeneGruppen);
    next.add(cursorGroup);
    return next;
  }, [offeneGruppen, currentId, groupOf]);

  /**
   * Die tatsaechlich gerenderte Zeilenliste: Gruppenkoepfe immer, ihre
   * Mitglieder nur, wenn die Gruppe offen ist. Ungruppierte Produkte
   * (`groupKey === null`) sind nie betroffen.
   */
  const visibleRows = useMemo(
    () =>
      allRows.filter(
        (row) => row.kind === 'group' || row.groupKey === null || effectiveOpenGroups.has(row.groupKey),
      ),
    [allRows, effectiveOpenGroups],
  );

  /**
   * Die Auswahl fuehrt nur ueber diese Menge: geladen UND unentschieden.
   *
   * Damit ist eine Auswahl, die einen Reiter- oder Filterwechsel ueberlebt
   * hat, folgenlos statt gefaehrlich — die Sammelhandlung kann kein Produkt
   * treffen, das gerade gar nicht in der Liste steht. Reihenfolge bleibt die
   * der Liste, damit eine Bestaetigung dieselbe Zahl nennt wie die Anzeige.
   */
  const validSelectedIds = useMemo(
    () =>
      products
        .filter((p) => selectedIds.has(p.id) && getReviewDecision(p.id) === 'pending_review')
        .map((p) => p.id),
    [products, selectedIds, getReviewDecision],
  );

  /**
   * Boxen gibt es NUR im Auswahlmodus, in jeder Breite (Spec 9.4).
   *
   * Vorher `!isNarrow || selectionMode` (breit standen sie dauerhaft). Zwei
   * Gruende, warum das weg ist. Erstens verlangt Spec 9.4 "im Normalfall
   * bleibt die Leiste ruhig", und zwar nicht nur auf dem Telefon - eine
   * Spalte Kaestchen neben jeder Zeile ist Dauerlaerm auf einer Flaeche, auf
   * der gelesen wird. Zweitens hat der neue Menueeintrag "Auswahl treffen"
   * breit sonst keine sichtbare Wirkung: er schaltet einen Modus ein, der
   * bereits an aussieht.
   */
  const checkboxesVisible = selectionMode;

  /**
   * Ob die Leiste dem Cursor folgt.
   *
   * Startet gebunden. Scrollt der Nutzer von Hand, wird die Bindung geloest —
   * sonst kaempft die automatische Rolle gegen jemanden, der gerade woanders
   * hinsieht. Statt sie still zu lassen, bietet ein Knopf sie zurueck an.
   */
  const [followsCursor, setFollowsCursor] = useState(true);

  const virtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  /**
   * Index in der GEMISCHTEN, sichtbaren Liste — nicht in `products`. Eine
   * Gruppenzeile zaehlt mit, das gesuchte Produkt kann also weiter hinten
   * stehen als sein Index in `products` vermuten liesse.
   */
  const currentIndex = currentId
    ? visibleRows.findIndex((row) => row.kind === 'product' && row.product.id === currentId)
    : -1;

  /**
   * Die Bindung zieht bei jedem Cursorwechsel nach. `scrollToIndex` ist eine
   * Ausgabe, kein Zustand — deshalb Effect und nicht Render.
   *
   * Zwei Feinheiten, beide notwendig:
   *
   * 1. Bei Index 0 wird NICHT gescrollt. Die Leiste steht dort ohnehin schon,
   *    und ein Scroll ohne Bewegung waere trotzdem ein `scroll`-Ereignis, das
   *    die Bindung gleich beim Aufbau wieder loesen wuerde — der Knopf
   *    "zurueck zur Position" staende dann von Anfang an da.
   * 2. Ein von uns ausgeloester Scroll darf die Bindung nicht loesen. Das
   *    Ereignis, das `scrollToIndex` erzeugt, ist von einem Nutzer-Scroll
   *    nicht zu unterscheiden; deshalb verbraucht `handleScroll` genau EIN
   *    Ereignis nach jedem programmatischen Sprung. Kein Zeitfenster: ein
   *    Timer verschluckt je nach Laufzeit einen echten Nutzer-Scroll und ist
   *    damit genau der Fehler, den er verhindern soll.
   */
  const programmaticScrollRef = useRef(false);
  useEffect(() => {
    if (!followsCursor || currentIndex <= 0) return;
    programmaticScrollRef.current = true;
    virtualizer.scrollToIndex(currentIndex, { align: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `virtualizer` entsteht pro Render neu; als Abhaengigkeit liefe der Effect endlos.
  }, [currentIndex, followsCursor]);

  const handleScroll = useCallback(() => {
    if (programmaticScrollRef.current) {
      programmaticScrollRef.current = false;
      return;
    }
    setFollowsCursor(false);
  }, []);

  const rebind = useCallback(() => {
    setFollowsCursor(true);
    if (currentIndex >= 0) {
      programmaticScrollRef.current = true;
      virtualizer.scrollToIndex(currentIndex, { align: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- siehe oben, `virtualizer` ist pro Render neu.
  }, [currentIndex]);

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col',
        /* Der Strich rechts war bisher die EINZIGE Kante der Liste. Traegt
           sie eine eigene Kachel, sitzt der Rahmen dort und der Strich waere
           eine zweite Linie unmittelbar daneben. */
        listenKachel === 'inKarte' && 'border-r border-border',
      )}
    >
      {/* Spec B.5: "Die Checkbox entfaellt im Normalzustand; 'Auswaehlen' im
          Kopf schaltet einen Auswahlmodus ein." Seit Spec 9.4 in JEDER Breite
          und nicht mehr nur schmal: die Boxen stehen breit nicht mehr
          dauerhaft (siehe `checkboxesVisible`), ohne den Schalter gaebe es
          dort also gar keinen Weg mehr in die Auswahl. Der frueher genannte
          Grund fuer die Bindung an `isNarrow` ("breit waere er ohne Wirkung")
          ist damit entfallen.
          Verlassen des Modus raeumt die Auswahl mit ab: eine unsichtbare
          Auswahl, die spaeter wieder auftaucht, ist genau die Klasse Fehler,
          gegen die die Filterung in `validSelectedIds` steht. */}
      {onSelectionModeChange && (
        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-border px-2 py-1.5">
          <span className="text-xs text-muted-foreground tabular-nums">
            {selectionMode
              ? t('rail.selectedCount', { count: validSelectedIds.length })
              : t('rail.label')}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onSelectionModeChange(!selectionMode)}
          >
            {selectionMode ? t('rail.selectDone') : t('rail.select')}
          </Button>
        </div>
      )}

      {/* V3-Umbau der Semantik: vorher role="listbox"/"option" — aber (a) in
          einer Option duerfen keine fokussierbaren Kinder stecken (die
          Auswahlbox ist ein <button>), (b) aria-selected meldete den CURSOR,
          waehrend die echte Mehrfachauswahl in den Boxen steckt, und (c) eine
          Listbox verspricht Pfeilnavigation, die es nicht gab. Jetzt eine
          ehrliche Liste: role="list"/"listitem", der Zeileninhalt ist ein
          echter <button> (damit per Tastatur erreichbar und aktivierbar,
          Roving Tabindex auf der Cursorzeile), der Cursor steht als
          aria-current am Knopf, die Auswahl ausschliesslich an den Boxen.
          ArrowUp/Down bewegen den Fokus zwischen den Zeilen — stopPropagation
          gehoert dazu, sonst blaettert der globale Handler nicht, aber
          kuenftige globale Belegungen wuerden hineinfunken. */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const aktiv = document.activeElement?.closest('[data-rail-row]');
            if (!aktiv) return;
            e.preventDefault();
            e.stopPropagation();
            // Der Index laeuft ueber die SICHTBARE, gemischte Liste —
            // Gruppenkoepfe zaehlen als Zeile mit, geschlossene Gruppen
            // ueberspringen ihre (nicht gerenderten) Mitglieder von selbst.
            const ziel = Number(aktiv.getAttribute('data-rail-row')) + (e.key === 'ArrowDown' ? 1 : -1);
            if (ziel < 0 || ziel >= visibleRows.length) return;
            const knopf = scrollRef.current?.querySelector<HTMLButtonElement>(`[data-rail-row="${ziel}"]`);
            if (knopf) {
              knopf.focus();
            } else {
              // Zeile noch nicht gerendert (Virtualisierung): erst hinscrollen,
              // im naechsten Frame fokussieren.
              programmaticScrollRef.current = true;
              virtualizer.scrollToIndex(ziel);
              requestAnimationFrame(() => {
                scrollRef.current?.querySelector<HTMLButtonElement>(`[data-rail-row="${ziel}"]`)?.focus();
              });
            }
            return;
          }

          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            // Nur auf einer Gruppenzeile etwas zu tun — ein Produktknopf
            // traegt kein `data-rail-group-key`.
            const aktiv = document.activeElement?.closest('[data-rail-row]');
            const gruppenSchluessel = aktiv?.getAttribute('data-rail-group-key');
            if (!gruppenSchluessel) return;
            e.preventDefault();
            e.stopPropagation();
            setOffeneGruppen((prev) => {
              const next = new Set(prev);
              if (e.key === 'ArrowRight') {
                next.add(gruppenSchluessel);
              } else {
                next.delete(gruppenSchluessel);
              }
              return next;
            });
          }
        }}
        role="list"
        aria-label={t('rail.label')}
        className="flex-1 min-h-0 overflow-y-auto"
      >
        <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row: RailRow<WorkbenchProduct> | undefined = visibleRows[virtualRow.index];
            if (!row) return null;

            const rowStyle: CSSProperties = {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: `${virtualRow.size}px`,
              transform: `translateY(${virtualRow.start}px)`,
            };

            if (row.kind === 'group') {
              const istOffen = effectiveOpenGroups.has(row.key);
              // B4: `variantenText` ist der sichtbare Zaehler ("N Varianten"),
              // getrennt gehalten, weil `aria-label` unten sowohl den
              // sichtbaren Text NACHBAUEN (ein `aria-label` ERSETZT den
              // zugaenglichen Namen, er ergaenzt ihn nicht) als auch den
              // Ein-/Ausklapp-Zustand aus `rail.expandGroup`/`collapseGroup`
              // anhaengen muss. Ohne den Nachbau wuerde der Praefix (`row.titel`)
              // und die Variantenzahl beim Vorlesen verloren gehen — genau die
              // beiden Angaben, die ein Screenreader-Nutzer braucht, um die
              // Gruppe von einer anderen zu unterscheiden.
              const variantenText = t('rail.variants', { count: row.count });
              const aufOderZu = istOffen ? t('rail.collapseGroup') : t('rail.expandGroup');
              return (
                <div
                  key={`gruppe-${row.key}`}
                  role="listitem"
                  style={rowStyle}
                  className="flex items-center border-b border-border px-2 transition-colors hover:bg-accent/50"
                >
                  <button
                    type="button"
                    data-rail-row={virtualRow.index}
                    data-rail-group-key={row.key}
                    aria-expanded={istOffen}
                    aria-label={`${row.titel}, ${variantenText}, ${aufOderZu}`}
                    tabIndex={-1}
                    onClick={() =>
                      setOffeneGruppen((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.key)) {
                          next.delete(row.key);
                        } else {
                          next.add(row.key);
                        }
                        return next;
                      })
                    }
                    className="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left"
                  >
                    {istOffen ? (
                      <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
                    )}
                    <span className="truncate text-xs text-muted-foreground">
                      {row.titel} · {variantenText}
                    </span>
                  </button>
                </div>
              );
            }

            const { product } = row;
            const isCurrent = product.id === currentId;
            // Die dringendste Regel dieses Produkts, uebersetzt genau wie
            // in `ValidationNotice` — dieselbe Funktion, damit die beiden
            // Anzeigen nie auseinanderlaufen.
            const urgentWarning = mostUrgentWarning(product.validation_warnings);

            return (
              <div
                key={product.id}
                role="listitem"
                style={rowStyle}
                className={cn(
                  'flex items-center gap-2 border-b border-border px-2 transition-colors',
                  // Mitglieder einer geoeffneten Gruppe stehen eingerueckt
                  // unter ihrem Gruppenkopf.
                  row.groupKey !== null && 'pl-6',
                  isCurrent ? 'bg-accent' : 'hover:bg-accent/50',
                )}
              >
                {/* Die Auswahlbox darf den Cursor NICHT bewegen: sonst springt
                    die Detailansicht bei jedem Haken weg und der Sammelmodus
                    wird unbenutzbar. */}
                {checkboxesVisible && (
                  /* V3-Befund: die Box ist 16 x 16 px — unter der normativen
                     AA-Untergrenze von 24 x 24, und auf dem Telefon die
                     einzige Bedienung des Auswahlmodus; ein Fehltipp traf die
                     Zeile und wechselte den Cursor. Ein <label> um die Box
                     macht die ganze Polsterflaeche zum Ziel (~40 x 40), ohne
                     die Optik zu aendern: Klick auf ein Label betaetigt sein
                     beschriftetes Bedienelement, und die Radix-Box ist ein
                     <button>, also beschriftbar. */
                  <label
                    onClick={(e) => e.stopPropagation()}
                    className="-m-3 flex-shrink-0 cursor-pointer p-3"
                  >
                    <Checkbox
                      checked={selectedIds.has(product.id)}
                      onCheckedChange={() => onToggleSelect(product.id)}
                      /* Entschiedene Zeilen sind nicht sammel-freigebbar; eine
                         waehlbare Box, die die Sammelhandlung danach
                         wegfiltert, waere eine Luege ueber die eigene
                         Wirkung. Gleiche Regel wie in der Listenansicht. */
                      disabled={getReviewDecision(product.id) !== 'pending_review'}
                      /* V3-Befund: der konstante Name "Produkt auswaehlen" an
                         hundert Boxen benennt sein Objekt nicht — ein
                         Screenreader-Nutzer hoert hundertmal dasselbe. Gleicher
                         Schluessel wie in der Listenansicht, die es seit jeher
                         richtig macht. */
                      aria-label={t('list.selectProduct', { title: product.optimized_title ?? '' })}
                    />
                  </label>
                )}

                <button
                  type="button"
                  data-rail-row={virtualRow.index}
                  onClick={() => onSelect(product.id)}
                  /* Der Cursor ist eine Position, keine Auswahl — aria-current
                     ist dafuer das richtige Vokabular. */
                  aria-current={isCurrent ? 'true' : undefined}
                  /* Roving Tabindex: EIN Tabstopp fuer die ganze Liste (die
                     Cursorzeile), innerhalb bewegen die Pfeile. 100 Tabstopps
                     waeren eine Tastaturwueste. */
                  tabIndex={isCurrent ? 0 : -1}
                  className="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                >
                <RailThumb imageLink={product.image_link} />

                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  {/* Einzeilig statt zweizeilig (T2): bei 44 statt 56 px
                      Zeilenhoehe hat ein zweiter Titel keinen Platz mehr. Der
                      Volltext gehoert ohnehin rechts gelesen, nicht hier. */}
                  <span className="truncate text-xs leading-tight">{product.optimized_title}</span>
                  {(product.brand || statusAnzeige === 'marker' || urgentWarning) && (
                    /* V3-Befund: muted auf der accent-Flaeche der AKTUELLEN
                       Zeile lag bei 3.84:1 hell / 4.09 dunkel — bei 10 px.
                       Auf der markierten Zeile traegt die Marke foreground
                       (14.6 / 14.3), auf allen anderen bleibt sie gedaempft.
                       Die Warnung daneben bleibt IMMER text-warning-ink,
                       unabhaengig vom Cursor-Zustand — sie ist ein Befund,
                       kein Zeilenschmuck. */
                    <span className="flex min-w-0 items-center gap-1.5 truncate text-[10px]">
                      {statusAnzeige === 'marker' && (
                        /* Der Marker ersetzt die Klartext-Pille aus der
                           Abzeichenspalte. Er steht HIER, weil die Markenzeile
                           ohnehin existiert und der Punkt damit null
                           Titelbreite kostet - die Pille kostete bis zu 144 px,
                           und dem Titel blieben 29 von 300. */
                        <ProductStatusPill
                          approvalStatus={getReviewDecision(product.id)}
                          uploadStatus={product.upload_status ?? null}
                          variante="marker"
                        />
                      )}
                      {product.brand && (
                        <span className={cn('truncate', isCurrent ? 'text-foreground' : 'text-muted-foreground')}>
                          {product.brand}
                        </span>
                      )}
                      {urgentWarning && (
                        <span className="truncate text-warning-ink">
                          {warningLabel(urgentWarning, t, (key) => i18n.exists(key))}
                        </span>
                      )}
                    </span>
                  )}
                </span>

                <span className="flex flex-shrink-0 items-center gap-1">
                  {/* Nackte Zahl statt Badge (T2): die Farbe traegt die
                      Semantik ueber `getScoreTextColor` — dieselbe Funktion,
                      die die Karte fuer Gesamtscore und Komponenten benutzt,
                      damit ein Score in der Leiste und in der Karte nie zwei
                      verschiedene Aussagen macht. */}
                  <span
                    className="text-xs font-medium tabular-nums"
                    style={
                      product.totalScore !== null
                        ? { color: getScoreTextColor(product.totalScore) }
                        : undefined
                    }
                  >
                    {product.totalScore !== null ? Math.round(product.totalScore) : '—'}
                  </span>
                  {statusAnzeige === 'pille' && (
                    <ProductStatusPill
                      approvalStatus={getReviewDecision(product.id)}
                      uploadStatus={product.upload_status ?? null}
                    />
                  )}
                </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Spec B.4: "Checkboxen in der Leiste, Sammelaktionsleiste unten."
          V1-Befund: die Boxen waren gebaut, diese Leiste nicht — wer in der
          Breite `normal` Haken setzte, konnte mit ihnen nichts tun. Sie sitzt
          bewusst im Fuss der LEISTE und nicht in der fixierten
          `WorkbenchActionBar`: die traegt die Handlungen fuer das EINE aktuelle
          Produkt, und zwei verschiedene Bezugsmengen in einer Leiste sind
          nicht ablesbar.

          Sichtbar nur mit gueltiger Auswahl. Ein dauerhafter Balken mit
          gesperrten Knoepfen kostet auf 300 px genau die Hoehe, die der Liste
          fehlt. */}
      {validSelectedIds.length > 0 && (onBulkApprove || onBulkReject) && (
        <div
          className="flex flex-shrink-0 flex-col gap-1 border-t border-border bg-muted/40 p-2"
          data-testid="rail-bulk-bar"
        >
          <p className="text-center text-[10px] text-muted-foreground tabular-nums">
            {t('rail.selectedCount', { count: validSelectedIds.length })}
          </p>
          <div className="flex gap-1">
            {onBulkReject && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 flex-1 gap-1 text-xs"
                onClick={() => setConfirmBulkReject(true)}
              >
                <X className="h-3 w-3" />
                {t('list.rejectSelected', { count: validSelectedIds.length })}
              </Button>
            )}
            {onBulkApprove && (
              /* V3-Befund: der Standard-Primaerknopf traegt weiss auf Indigo —
                 4.41:1 hell, 2.95 dunkel (selbst nachgerechnet). Die
                 Aktionsleiste loest ihr "Freigeben" laengst ueber die
                 success-Flaeche (4.76 / 10.18); derselbe Anstrich hier haelt
                 die Freigabe-Knoepfe der Werkbank auch optisch konsistent. */
              <Button
                variant="default"
                size="sm"
                className="h-7 flex-1 gap-1 text-xs bg-success hover:bg-success-hover text-success-foreground"
                onClick={() => onBulkApprove(validSelectedIds)}
              >
                <Check className="h-3 w-3" />
                {t('list.approveSelected', { count: validSelectedIds.length })}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Rueckfrage vor der Sammel-Ablehnung, gleiches Muster wie in der
          Listenansicht: abgelehnt ist unumkehrbar teuer, freigegeben nicht. */}
      <AlertDialog open={confirmBulkReject} onOpenChange={setConfirmBulkReject}>
        <AlertDialogContent
          onCloseAutoFocus={() => {
                      /* V3-Befund: Radix gibt den Fokus an das zuvor fokussierte
                         Element zurueck — das kann durch die BESTAETIGTE Handlung
                         verschwunden sein (letztes Produkt des Reiters, geleerte
                         Auswahl). Dann faellt der Fokus auf <body> und Tab beginnt
                         wieder ganz oben. Nach dem Standardverhalten pruefen, und
                         falls nichts angekommen ist, auf den aktiven Reiter setzen:
                         der existiert immer. */
                      queueMicrotask(() => {
              /* Microtask statt setTimeout: ein Timer kann den Unmount (und
                 in Tests den Abbau der DOM-Umgebung) ueberleben — genau das
                 hat den CI-Lauf mit "document is not defined" gerissen,
                 waehrend die Suite lokal nur durch Timing gruen war. Die
                 Microtask laeuft noch im selben Task, direkt nach Radix'
                 eigener Fokus-Rueckgabe, und kann nichts ueberleben. */
              if (typeof document === 'undefined') return;
              if (document.activeElement === document.body) {
                document
                  .querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
                  ?.focus();
              }
            });
                    }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('bulkRejectConfirm.title', { count: validSelectedIds.length })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkRejectConfirm.body', { count: validSelectedIds.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('bulkRejectConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmBulkReject(false);
                onBulkReject?.(validSelectedIds);
              }}
            >
              {t('bulkRejectConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-shrink-0 flex-col gap-1 border-t border-border p-2">
        {!followsCursor && (
          <Button variant="outline" size="sm" onClick={rebind} className="h-7 gap-1 text-xs">
            <Crosshair className="h-3 w-3" />
            {t('rail.backToPosition')}
          </Button>
        )}

        {/* Ehrlicher Ladestand: die Leiste laedt seitenweise, die Zahl rechts
            ist der Feed. `null` heisst unbekannt und wird nicht als Zahl
            ausgegeben. */}
        <p className="text-center text-[10px] text-muted-foreground tabular-nums">
          {pendingTotal === null
            ? t('rail.loadedUnknownTotal', { count: loadedCount })
            : t('rail.loaded', {
                loaded: loadedCount.toLocaleString(numberLocale(i18n.language)),
                total: pendingTotal.toLocaleString(numberLocale(i18n.language)),
              })}
        </p>

        {hasNextPage && (
          <Button variant="ghost" size="sm" onClick={onLoadMore} className="h-7 text-xs">
            {t('rail.loadMore')}
          </Button>
        )}
      </div>
    </div>
  );
}
